import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { debtAdjustments } from '@/api/debtAdjustments';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { refs } from '@/api/refs';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}

const TITLE: Record<string, string> = {
  MUTUAL_OFFSET: 'Акт зарахування зустрічних однорідних вимог',
  TRANSFER: 'Акт переносу заборгованості',
  WRITE_OFF: 'Бухгалтерська довідка про списання заборгованості',
  OFF_BALANCE_WRITEOFF: 'Бухгалтерська довідка про списання з забалансу',
};

export default function DebtAdjustmentPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['debt-adjustment', id ?? ''],
    queryFn: () => debtAdjustments.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });

  // Унікальні contractor_id з рядків — для назв договорів
  const ctrIds = useMemo(() => {
    const s = new Set<string>();
    docQ.data?.items.forEach((it) => {
      s.add(it.debtor_contractor_id);
      s.add(it.creditor_contractor_id);
    });
    return Array.from(s);
  }, [docQ.data]);

  const contractsQuery = useQuery({
    queryKey: ['contracts-all-for-print', docQ.data?.organization_id, ctrIds.join(',')],
    queryFn: async () => {
      const all = await Promise.all(
        ctrIds.map((cid) =>
          refs.contracts({ organization_id: docQ.data!.organization_id, contractor_id: cid })
        ),
      );
      const map: Record<string, { id: string; code: string; name: string }> = {};
      all.flat().forEach((c) => { map[c.id] = c; });
      return map;
    },
    enabled: !!docQ.data && ctrIds.length > 0,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc.organization_id);
  const ctrName = (id: string) => ctrQ.data?.items.find((c) => c.id === id)?.name ?? '—';
  const ctrFull = (id: string) => ctrQ.data?.items.find((c) => c.id === id);
  const cntName = (id: string) => {
    const c = contractsQuery.data?.[id];
    return c ? `${c.code} — ${c.name}` : '—';
  };

  const title = TITLE[doc.adjustment_type] ?? 'Документ коригування боргу';
  const isWriteOff = doc.adjustment_type === 'WRITE_OFF';

  // Для MUTUAL_OFFSET зазвичай дві сторони — наша і контрагент.
  // Але в нашій моделі обидві сторони — це КОНТРАГЕНТИ (одна з якою у нас
  // дебіторка, інша — кредиторка). Тобто наша організація — посередник.
  // Покажемо обидві сторони + нашу організацію.

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '11pt', lineHeight: 1.4,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '10pt' }}>{org?.full_name ?? org?.name ?? ''}</div>
        {org?.edrpou && (
          <div style={{ fontSize: '10pt', color: '#555' }}>ЄДРПОУ: {org.edrpou}</div>
        )}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '1rem 0 0.3rem' }}>
        {title}
      </h2>
      <h3 style={{ textAlign: 'center', fontSize: '11pt', margin: '0 0 1rem', fontWeight: 'normal' }}>
        № {doc.document_number} від {formatDateUk(doc.document_date)}
      </h3>

      {doc.operation_content && (
        <p style={{ marginBottom: '1rem' }}>
          <strong>Підстава:</strong> {doc.operation_content}
        </p>
      )}

      {doc.adjustment_type === 'MUTUAL_OFFSET' && (
        <p style={{ marginBottom: '1rem' }}>
          Між {org?.full_name ?? org?.name ?? '—'} (далі «Сторона»),
          з одного боку, та контрагентами, переліченими нижче, з іншого боку,
          складено цей Акт про те, що сторони здійснюють зарахування
          зустрічних однорідних вимог на загальну суму
          <strong> {fmt(doc.total_amount)} грн </strong>
          у такому розрізі:
        </p>
      )}

      {doc.adjustment_type === 'TRANSFER' && (
        <p style={{ marginBottom: '1rem' }}>
          Цим актом переноситься заборгованість з однієї пари
          «контрагент-договір» на іншу на загальну суму
          <strong> {fmt(doc.total_amount)} грн</strong>.
        </p>
      )}

      {isWriteOff && (
        <p style={{ marginBottom: '1rem' }}>
          {doc.use_reserve
            ? 'Списання заборгованості за рахунок раніше створеного резерву сумнівних боргів.'
            : 'Безповоротне списання заборгованості, визнаної безнадійною (або прощеної кредиторської заборгованості).'}
          {doc.record_off_balance && (
            <> Списана дебіторка обліковується на забалансовому
            рахунку 071 протягом 3 років.</>
          )}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
        <thead>
          <tr style={{ borderTop: '2px solid #000', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '0.3rem', textAlign: 'center', width: '4%' }}>№</th>
            <th style={{ padding: '0.3rem', textAlign: 'left' }}>
              {isWriteOff ? 'Контрагент (борг якого списується)' : 'Дебітор (контрагент, що винен нам)'}
            </th>
            <th style={{ padding: '0.3rem', textAlign: 'left' }}>
              {isWriteOff ? 'Рах. витрат/доходу' : 'Кредитор (наш борг кому)'}
            </th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '14%' }}>Сума</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it) => (
            <tr key={it.id} style={{ borderBottom: '1px solid #ccc' }}>
              <td style={{ padding: '0.3rem', textAlign: 'center' }}>{it.line_number}</td>
              <td style={{ padding: '0.3rem' }}>
                <div><strong>{ctrName(it.debtor_contractor_id)}</strong></div>
                <div style={{ fontSize: '9pt', color: '#555' }}>
                  Договір: {cntName(it.debtor_contract_id)}
                </div>
                <div style={{ fontSize: '9pt', color: '#555' }}>
                  Рахунок: {it.debtor_account_code}
                </div>
              </td>
              <td style={{ padding: '0.3rem' }}>
                {isWriteOff ? (
                  <div style={{ fontSize: '10pt' }}>
                    {it.creditor_account_code}
                    {doc.use_reserve && <em style={{ color: '#555' }}> (резерв сумнівних)</em>}
                  </div>
                ) : (
                  <>
                    <div><strong>{ctrName(it.creditor_contractor_id)}</strong></div>
                    <div style={{ fontSize: '9pt', color: '#555' }}>
                      Договір: {cntName(it.creditor_contract_id)}
                    </div>
                    <div style={{ fontSize: '9pt', color: '#555' }}>
                      Рахунок: {it.creditor_account_code}
                    </div>
                  </>
                )}
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600 }}>
                {fmt(it.amount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #000', fontWeight: 700 }}>
            <td colSpan={3} style={{ padding: '0.3rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(doc.total_amount)}</td>
          </tr>
        </tfoot>
      </table>

      {doc.adjustment_type === 'MUTUAL_OFFSET' && (
        <>
          <p style={{ marginTop: '1.5rem' }}>
            Після підписання цього Акту зобов'язання сторін за вищезазначеними
            підставами вважаються повністю припиненими шляхом зарахування
            зустрічних однорідних вимог відповідно до ст. 601 Цивільного кодексу України.
          </p>

          <table style={{ width: '100%', marginTop: '2rem', borderCollapse: 'collapse' }}>
            <tbody>
              {(() => {
                // Зберемо унікальних контрагентів для підписів
                const uniqueCtrs = new Map<string, { contractor_id: string }>();
                doc.items.forEach((it) => {
                  uniqueCtrs.set(it.debtor_contractor_id, { contractor_id: it.debtor_contractor_id });
                  uniqueCtrs.set(it.creditor_contractor_id, { contractor_id: it.creditor_contractor_id });
                });
                const list = Array.from(uniqueCtrs.values());
                // Виводимо попарно (org + кожен контрагент). Найчастіше зачет
                // двосторонній: 1 контрагент-постачальник + 1 контрагент-покупець.
                return (
                  <tr>
                    <td style={{ width: '50%', padding: '0.5rem', verticalAlign: 'top' }}>
                      <strong>{org?.name ?? '—'}</strong>
                      {org?.edrpou && <div style={{ fontSize: '9pt', color: '#555' }}>ЄДРПОУ: {org.edrpou}</div>}
                      <div style={{ marginTop: '2rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                        __________________
                      </div>
                      <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
                      <div style={{ marginTop: '0.3rem' }}>М.П.</div>
                    </td>
                    <td style={{ width: '50%', padding: '0.5rem', verticalAlign: 'top' }}>
                      {list.map((c) => {
                        const full = ctrFull(c.contractor_id);
                        return (
                          <div key={c.contractor_id} style={{ marginBottom: '1.5rem' }}>
                            <strong>{full?.name ?? '—'}</strong>
                            {full?.edrpou && <div style={{ fontSize: '9pt', color: '#555' }}>ЄДРПОУ: {full.edrpou}</div>}
                            <div style={{ marginTop: '1.5rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                              __________________
                            </div>
                            <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
                            <div style={{ marginTop: '0.3rem' }}>М.П.</div>
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </>
      )}

      {isWriteOff && (
        <table style={{ width: '100%', marginTop: '2.5rem', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ width: '50%', padding: '0.5rem' }}>
                <div>Керівник:</div>
                <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                  ___________________
                </div>
                <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              </td>
              <td style={{ width: '50%', padding: '0.5rem' }}>
                <div>Головний бухгалтер:</div>
                <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center' }}>
                  ___________________
                </div>
                <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {doc.adjustment_type === 'TRANSFER' && (
        <p style={{ marginTop: '1.5rem' }}>
          Цей акт є підставою для бухгалтерського обліку перенесення
          заборгованості і не є самостійним правочином сторін.
        </p>
      )}

      <div className="print-toolbar" style={{
        position: 'fixed', bottom: '1rem', right: '1rem',
        background: 'white', padding: '0.5rem', borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <button onClick={() => window.print()} className="primary">🖨 Друкувати</button>
      </div>
    </div>
  );
}
